import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { PoComboFilter, PoComboOption } from '@po-ui/ng-components';

import { GitService } from '../../../core/api/git.service';
import { providerKeyForTerm, sourceProviderLabel } from '../../../core/utils/provider-label.util';
import { GitRepository, ReviewableRepositories } from '../../../core/interfaces/git.interface';
import { getApiErrorMessage } from '../../../core/utils/http-error-message.util';

/**
 * Alimenta o combo de repositórios do passo git.
 *
 * A busca vai ao SERVIDOR a cada digitação, com debounce no combo. Não é preferência de
 * arquitetura: a lista responde "o que EU alcanço", e para respondê-la o backend pergunta
 * ao provedor Git, um repositório por vez. Trazer o cadastro inteiro para filtrar aqui
 * obrigaria a sondar o provedor sobre tudo o que seria descartado — com trezentos
 * cadastros, segundos de espera no primeiro paint e rate limit no provedor quando várias
 * pessoas abrem a tela ao mesmo tempo.
 *
 * A consequência é que a lista é um RECORTE, e `truncated` diz quando ela foi cortada.
 */
@Injectable()
export class GitRepoComboFilterService implements PoComboFilter {
  /**
   * Avisa o passo sobre o resultado da última busca: mensagem quando falha, string vazia
   * quando dá certo.
   *
   * O contrato do PoComboFilter só admite devolver opções, então um erro viraria lista
   * vazia — indistinguível, para quem olha, de "você não tem repositório nenhum". A falha
   * precisa chegar à tela por fora.
   */
  notify: ((error: string) => void) | null = null;

  /**
   * Avisa o passo de que a resposta veio cortada, para a tela pedir um termo mais
   * específico.
   *
   * Sai por um canal próprio, e não junto do erro, porque não é erro: a lista está certa,
   * só não está inteira. Misturar os dois faria a tela mostrar aviso de falha para uma
   * busca que funcionou.
   */
  notifyTruncated: ((truncated: boolean) => void) | null = null;

  /**
   * Repositórios já devolvidos, por fullName. O combo entrega de volta apenas o valor
   * escolhido, e o passo seguinte precisa do objeto inteiro — owner, name e slug — para
   * pedir os Pull Requests e para montar o review.
   *
   * Acumula entre buscas em vez de ser substituído: o que a pessoa escolheu numa busca
   * precisa continuar resolvível depois de ela digitar outro termo, e ao voltar do passo
   * de Pull Request.
   */
  private readonly byValue = new Map<string, GitRepository>();

  constructor(private gitService: GitService) {}

  /**
   * Anota o repositório como usado agora, para aparecer primeiro da próxima vez que o
   * campo abrir vazio.
   *
   * Chamado só quando a pessoa efetivamente ESCOLHE um repositório — não quando ele
   * aparece numa busca. GitHub e VS Code fazem o mesmo: mostram o que foi usado, não o
   * que foi visto.
   */
  noteRecentSelection(repo: GitRepository): void {
    rememberRecentRepo(repo.fullName);
  }

  /**
   * O termo digitado chega em `params.value` — é o contrato do PoComboFilter, que
   * documenta "a propriedade e o valor". Ler outro nome faz o filtro ser sempre vazio.
   *
   * O termo é repassado cru ao servidor, que casa slug, organização, projeto, nome e
   * provedor. O provedor entra na busca porque entrou no rótulo: quem vê "Gitea" na linha
   * espera digitar "gitea" e estreitar a lista.
   */
  getFilteredData(params: { property?: string; value?: string }): Observable<PoComboOption[]> {
    return this.search(params.value || '').pipe(
      map(repos => repos.map(repo => ({ label: labelFor(repo), value: repo.fullName })))
    );
  }

  /**
   * Primeira página, para o passo poder falar sobre a lista antes de qualquer digitação.
   *
   * O combo só chama `getFilteredData` quando alguém digita ou abre a lista. Sem isto, um
   * ambiente sem nenhum repositório cadastrado abriria um campo vazio e silencioso — que
   * é justamente o estado inicial de toda instalação, agora que o cadastro é obrigatório.
   */
  preload(): Observable<GitRepository[]> {
    return this.search('');
  }

  /**
   * Consulta o servidor e guarda o que veio.
   *
   * A falha vira lista vazia porque o contrato do PoComboFilter não admite outra coisa —
   * por isso ela também sai por `notify`, senão "deu erro" e "você não tem repositório
   * cadastrado" ficariam idênticos na tela.
   */
  private search(term: string): Observable<GitRepository[]> {
    // "Azure DevOps" é como o provedor aparece na linha; no cadastro ele é `azuredevops`.
    // Quem digita o que leu precisa achar o mesmo que quem digita o identificador.
    const query = providerKeyForTerm(term) || term;

    return this.gitService.listReviewableRepositories(query).pipe(
      tap((res: ReviewableRepositories) => {
        res.repositories.forEach(repo => this.byValue.set(repo.fullName, repo));
        this.notify?.('');
        this.notifyTruncated?.(res.truncated);
      }),
      map(res => {
        const sorted = [...res.repositories].sort(compareForList);
        // Recente só entra à frente na busca VAZIA — a pessoa abrindo o passo sem ter
        // digitado nada. Numa busca de verdade, a ordem tem que responder ao termo
        // digitado, não a quem foi usado por último.
        return term.trim() ? sorted : withRecentFirst(sorted);
      }),
      catchError(err => {
        this.notify?.(getApiErrorMessage(err, 'Não foi possível listar os repositórios cadastrados.'));
        this.notifyTruncated?.(false);
        return of([] as GitRepository[]);
      })
    );
  }

  /**
   * O PO chama isto ao inicializar o combo com um valor já escolhido — o que acontece
   * quando se volta do passo de Pull Request. Responder pelo que já foi devolvido evita
   * uma ida à API para redescobrir o que acabou de ser escolhido.
   *
   * Sem cache, busca pelo ÚLTIMO segmento do fullName — o nome do repositório —, não
   * pelo fullName inteiro. O servidor casa o termo contra slug, org, project, name e
   * provider como campos SEPARADOS; `owner/project/name` com barra não é o valor de
   * nenhum deles, e mandaria a busca de volta vazia. O `find` abaixo ainda confere o
   * fullName exato, para não confundir dois repositórios de mesmo nome em organizações
   * diferentes.
   */
  getObjectByValue(value: string | number): Observable<PoComboOption> {
    const fullName = String(value);
    const cached = this.byValue.get(fullName);
    if (cached) { return of({ label: labelFor(cached), value: fullName }); }

    const name = fullName.split('/').pop() || fullName;
    return this.search(name).pipe(
      map(repos => {
        const found = repos.find(repo => repo.fullName === fullName);
        return found ? { label: labelFor(found), value: fullName } : { label: fullName, value };
      }),
      catchError(() => of({ label: fullName, value }))
    );
  }

  /** Devolve o repositório por trás do valor escolhido. */
  repositoryFor(value: string): GitRepository | null {
    return this.byValue.get(value) ?? null;
  }

  /** Esquece o que foi carregado. Chamado ao sair do fluxo git, junto do resto do estado. */
  reset(): void {
    this.byValue.clear();
  }
}

/**
 * Ordena a lista como ela é LIDA: provedor primeiro, depois o caminho.
 *
 * O backend devolve por slug, o que é a ordem certa para ele. Aqui o rótulo começa pelo
 * provedor, então a ordem por slug fazia a coluna que o olho lê primeiro variar sem
 * critério aparente — com três repositórios não incomoda, com cinquenta é uma lista que
 * não se varre. Ordenar pelo que está escrito na frente agrupa os blocos.
 */
function compareForList(a: GitRepository, b: GitRepository): number {
  const byProvider = sourceProviderLabel(a.provider).localeCompare(sourceProviderLabel(b.provider), 'pt-BR');
  return byProvider !== 0 ? byProvider : a.fullName.localeCompare(b.fullName, 'pt-BR');
}

/**
 * Como o repositório aparece na linha do combo.
 *
 * O provedor vem primeiro porque é o que separa dois repositórios de mesmo nome em
 * organizações diferentes — e porque a jornada deixou de ter um passo só para escolhê-lo:
 * a informação passou a viver aqui, onde ela é lida junto do repositório em vez de antes
 * dele.
 */
function labelFor(repo: GitRepository): string {
  const parts: string[] = [];
  const provider = sourceProviderLabel(repo.provider);
  if (provider) { parts.push(provider); }
  parts.push(repo.fullName);
  if (repo.defaultBranch) { parts.push(repo.defaultBranch); }
  return parts.join(' · ');
}

/**
 * Chave e teto dos "recentes", guardados no `localStorage` — por navegador, não por
 * usuário do backend. É conveniência de tela, não estado que precisa ser correto entre
 * dispositivos.
 */
export const RECENT_REPOS_KEY = 'smart-review.recentGitRepos';
const MAX_RECENT_REPOS = 5;

/** Lê os fullName recentes, do mais novo para o mais velho. Nunca lança. */
function readRecentRepos(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_REPOS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    // Janela privada, storage bloqueado ou JSON corrompido: sem recentes é o
    // degradado certo, não motivo para quebrar o combo.
    return [];
  }
}

/** Põe `fullName` na frente dos recentes, sem duplicar, cortado em MAX_RECENT_REPOS. */
function rememberRecentRepo(fullName: string): void {
  try {
    const withoutIt = readRecentRepos().filter(v => v !== fullName);
    localStorage.setItem(RECENT_REPOS_KEY, JSON.stringify([fullName, ...withoutIt].slice(0, MAX_RECENT_REPOS)));
  } catch {
    // Mesma degradação de readRecentRepos: não ter "recentes" nunca pode impedir a
    // escolha do repositório em si.
  }
}

/**
 * Põe à frente da lista os repositórios recentes que aparecem nela, na ordem de uso mais
 * recente primeiro, seguidos do resto na ordem que já vinha.
 *
 * Só reordena dentro do que o SERVIDOR já devolveu — não é uma promessa de sempre mostrar
 * todo recente independente do tamanho do cadastro. Um recente que não está entre os
 * primeiros resultados da busca vazia simplesmente não aparece na frente; ele continua
 * alcançável digitando o nome, como qualquer outro.
 */
function withRecentFirst(repos: GitRepository[]): GitRepository[] {
  const recent = readRecentRepos();
  if (!recent.length) { return repos; }

  const byFullName = new Map(repos.map(repo => [repo.fullName, repo]));
  const front = recent.map(fullName => byFullName.get(fullName)).filter((r): r is GitRepository => !!r);
  const frontNames = new Set(front.map(repo => repo.fullName));
  return [...front, ...repos.filter(repo => !frontNames.has(repo.fullName))];
}
